import React, { useEffect, useState } from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  createEmptyMatcher,
  createEmptySegment,
  isSegmentValidForPreview,
  type BreakdownGroupDescriptor,
  type SegmentDescriptor,
} from "@north-beam/nb-common";
import { TertiaryButton } from "@shared/buttons";
import { Switch } from "@shared/switch";

import { FilterSelector } from "./filter-selector";
import { MatcherRenderer } from "./matcher-renderer";
import { SegmentOperatorSelector } from "./segment-operator-selector";

interface Props {
  showActiveSwitch?: boolean;
  breakdownGroup: BreakdownGroupDescriptor;
  segment: SegmentDescriptor;
  index: number;
  totalSegments: number;
  onChange: (value: SegmentDescriptor) => void;
  onDelete: () => void;
  onClickShowMatches: () => void;
}

export const SegmentRow = ({
  showActiveSwitch,
  breakdownGroup,
  segment,
  index,
  totalSegments,
  onChange,
  onDelete,
  onClickShowMatches,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: segment.id,
    disabled: totalSegments === 1,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [aboutToDelete, setAboutToDelete] = useState(false);
  const [isNameInputFocused, setNameInputFocused] = useState(false);
  const [name, setName] = useState(segment.name);

  useEffect(() => setName(segment.name), [segment.name]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx("border rounded p-2 group/segment", {
        "relative shadow-sm z-[1]": isDragging,
        "border-gray-2 bg-white hover:border-gray": !aboutToDelete,
        "border-mars-2 bg-mars-2": aboutToDelete,
      })}
    >
      {aboutToDelete ? (
        <div className="flex items-center gap-3 h-8">
          <div className="flex items-center gap-2">
            <div
              ref={setActivatorNodeRef}
              {...attributes}
              {...listeners}
              className={clsx(
                "rounded-md w-6 h-6 flex items-center justify-center",
                {
                  "cursor-grab": totalSegments > 1 && !isDragging,
                  "cursor-grabbing": isDragging,
                  "text-gray-2 cursor-auto": totalSegments === 1,
                  "text-gray hover:bg-cosmic-3 hover:text-cosmic":
                    totalSegments > 1,
                },
              )}
            >
              <i className="fa-regular fa-grip-dots font-bold" />
            </div>
            <i className="fa-solid fa-arrow-turn-down-right text-gray-2" />
          </div>
          <div className="flex-1 text-sm font-bold">
            {segment.name
              ? `Delete segment "${segment.name}"?`
              : "Delete segment?"}
          </div>
          <div className="space-x-2">
            <TertiaryButton
              onClick={() => {
                prettyLogEventPage({
                  domain: "Breakdown v2",
                  action: "onClick",
                  additionalActionInfo: "Delete segment",
                  data: {
                    pageContext: { segment },
                  },
                });

                onDelete();
              }}
              className="text-mars font-bold rounded-md h-7 py-0 hover:bg-[white]"
            >
              YES, DELETE
            </TertiaryButton>
            <TertiaryButton
              className="font-bold rounded-md h-7 py-0 hover:bg-[white]"
              onClick={() => setAboutToDelete(false)}
            >
              CANCEL
            </TertiaryButton>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <div className="flex items-center gap-2 h-8">
            <div
              ref={setActivatorNodeRef}
              {...attributes}
              {...listeners}
              className={clsx(
                "rounded-md w-6 h-6 flex items-center justify-center",
                {
                  "cursor-grab": totalSegments > 1 && !isDragging,
                  "cursor-grabbing": isDragging,
                  "text-gray-2 cursor-auto": totalSegments === 1,
                  "text-gray hover:bg-cosmic-3 hover:text-cosmic":
                    totalSegments > 1,
                },
              )}
            >
              <i className="fa-regular fa-grip-dots font-bold" />
            </div>
            {showActiveSwitch ? (
              <Switch
                id={`switch-segment-active-${segment.id}`}
                size="small"
                value={segment.active}
                onChange={(value) => {
                  prettyLogEventPage({
                    domain: "Breakdown v2",
                    action: "onChange",
                    additionalActionInfo: "Change segment active",
                    data: {
                      newActive: value,
                      pageContext: { segment },
                    },
                  });

                  onChange({ ...segment, active: value });
                }}
              />
            ) : (
              <i className="fa-solid fa-arrow-turn-down-right text-gray-2" />
            )}
          </div>
          <div className="flex-1 flex items-start gap-2">
            <div className="space-y-1">
              <div
                className={clsx(
                  "w-[200px] flex items-center border rounded shrink-0",
                  {
                    "border-gray hover:border-interstellar":
                      !isNameInputFocused,
                    "border-cosmic": !segment.error && isNameInputFocused,
                    "border-mars": segment.error,
                  },
                )}
              >
                <input
                  className={clsx("text-sm rounded h-[30px] px-2 w-full")}
                  type="text"
                  placeholder={`Segment ${index + 1}`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onFocus={() => setNameInputFocused(true)}
                  onBlur={() => {
                    prettyLogEventPage({
                      domain: "Breakdown v2",
                      action: "onChange",
                      additionalActionInfo: "Change segment name",
                      data: {
                        newName: name,
                        pageContext: { segment },
                      },
                    });

                    if (name === "") {
                      onChange({
                        ...createEmptySegment(),
                        id: segment.id,
                      });
                    } else {
                      onChange({ ...segment, name });
                    }

                    setNameInputFocused(false);
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      e.currentTarget.blur();
                    }
                  }}
                />
                {isNameInputFocused && (name !== "" || segment.name !== "") && (
                  <TertiaryButton className="h-6 py-0 px-1.5 mr-[3px] rounded font-bold">
                    {segment.name === "" ? "Add" : "Done"}
                  </TertiaryButton>
                )}
              </div>
              {segment.error && (
                <div className="text-xxs text-mars">*{segment.error}</div>
              )}
            </div>
            <div className="flex flex-wrap items-start gap-2">
              {segment.name !== "" && (
                <>
                  {segment.matchers.map((matcher, i) => {
                    return (
                      <div key={matcher.id} className="flex items-start gap-2">
                        <MatcherRenderer
                          matcher={matcher}
                          onChange={(value) => {
                            prettyLogEventPage({
                              domain: "Breakdown v2",
                              action: "onChange",
                              additionalActionInfo: "Change segment criteria",
                            });

                            onChange({
                              ...segment,
                              matchers: segment.matchers.map((e) =>
                                e === matcher ? value : e,
                              ),
                            });
                          }}
                          onDelete={() => {
                            const matchers = segment.matchers.filter(
                              (e) => e !== matcher,
                            );

                            prettyLogEventPage({
                              domain: "Breakdown v2",
                              action: "onChange",
                              additionalActionInfo: "Delete segment criteria",
                            });

                            onChange({ ...segment, matchers });
                          }}
                        />
                        {i < segment.matchers.length - 1 && (
                          <SegmentOperatorSelector
                            value={segment.operator}
                            onChange={(operator) => {
                              prettyLogEventPage({
                                domain: "Breakdown v2",
                                action: "onChange",
                                additionalActionInfo: "Change segment operator",
                              });

                              onChange({ ...segment, operator });
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                  <TertiaryButton
                    className="flex items-center gap-2 font-bold"
                    onClick={() => {
                      prettyLogEventPage({
                        domain: "Breakdown v2",
                        action: "onClick",
                        additionalActionInfo: "Add segment criteria",
                      });

                      onChange({
                        ...segment,
                        matchers: [...segment.matchers, createEmptyMatcher()],
                      });
                    }}
                  >
                    <i className="fa-regular fa-circle-plus text-cosmic" />
                    {segment.matchers.length > 0
                      ? "ADD CRITERIA"
                      : "DEFINE CRITERIA"}
                  </TertiaryButton>
                  <FilterSelector
                    breakdownGroup={breakdownGroup}
                    level={segment.level}
                    filters={segment.filters}
                    onChange={(level, filters) => {
                      prettyLogEventPage({
                        domain: "Breakdown v2",
                        action: "onChange",
                        additionalActionInfo: "Change segment filters",
                      });

                      onChange({ ...segment, level, filters });
                    }}
                  />
                </>
              )}
            </div>
          </div>
          <div className="h-8 flex items-center gap-1">
            {isSegmentValidForPreview(segment) && (
              <TertiaryButton
                onClick={onClickShowMatches}
                className={clsx(
                  "flex items-center gap-2 font-bold invisible group-hover/segment:visible",
                )}
              >
                <i className="fa-regular fa-eye" />
                SHOW MATCHES
              </TertiaryButton>
            )}
            {totalSegments > 1 && (
              <div className="h-8 flex items-center">
                <GenericTooltip noInfoCircle content="Delete segment">
                  <TertiaryButton
                    className={clsx(
                      "flex items-center justify-center rounded-md w-6 h-6 p-0 hover:bg-mars-2",
                      "invisible group-hover/segment:visible",
                    )}
                    onClick={() => setAboutToDelete(true)}
                  >
                    <i className="fa-regular fa-times-circle text-mars" />
                  </TertiaryButton>
                </GenericTooltip>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
